import { GlobalConfigType } from 'actions/global.config.type'
import cloneDeep from 'clone-deep'
import {
  FieldOrigin,
  InputType,
  IVisibleProp,
  LanguageOptions,
  LoanFieldProps,
  loanOptionsConstants,
  MaritalStatusTypes,
  OwnershipTypes,
  OwnRentYearOptions,
  YearOptions,
} from 'config'
import { isCustomFieldKey } from 'config/loan.input.fields.constants'
import { store } from 'reducers'
import { getNewSelectionOptions } from 'utils/convertor'
import { applyOptionVisibleLogic, filterOptionsByVisibleLogic } from 'utils/loan'

export const defaultInputs = (_fieldProps: Record<string, IVisibleProp> | null = null): Record<string, InputType> => {
  const { globalConfigReducer } = store.getState()

  const inputs: Record<string, InputType> = {
    hasEntityTitle: {
      inputType: 'toggle',
      title: 'Entity Information',
    },
    entityTitle: {
      inputType: 'text',
      title: 'Entity Name',
      fontSize: 24,
      required: true,
    },
    entityAddress: {
      inputType: 'map',
      title: 'Entity Address',
      required: true,
    },
    entityTaxID: {
      inputType: 'text',
      title: 'Entity Tax ID',
      type: 'entityTaxID',
      required: true,
    },
    entityState: {
      title: 'State of Entity',
      inputType: 'select',
      hasDefaultOption: true,
      options: [...loanOptionsConstants.states],
      error: '',
      required: true,
    },
    borrowerType: {
      title: 'Borrower Type',
      inputType: 'Select',
      options: loanOptionsConstants.borrowerType,
      error: '',
      required: true,
      hasDefaultOption: true,
    },
    borrowerPosition: {
      title: 'Borrower Position',
      inputType: 'Select',
      options: [
        'General Partner',
        'Limited Partner',
        'Managing Member',
        'Manager',
        'Member',
        'President',
        'Vice President',
        'Secretary',
        'Sole Member',
        'Treasurer',
        'Trustee',
      ],
      error: '',
      required: true,
      hasDefaultOption: true,
    },
    ownershipPercentage: {
      inputType: 'text',
      type: 'number',
      maxNumberRange: 100,
      prefix: '%',
      title: 'Ownership Percentage',
      required: true,
    },
    isLayeredEntity: {
      inputType: 'ToggleButton',
      title: 'Is Layered Entity?',
    },
    layerCount: {
      inputType: 'text',
      type: 'number',
      title: 'How Many Layers?',
      required: true,
    },
    firstName: {
      inputType: 'text',
      title: 'First Name',
      required: true,
    },
    middleName: {
      inputType: 'text',
      title: 'Middle Name',
    },
    lastName: {
      inputType: 'text',
      title: 'Last Name',
      required: true,
    },
    residency: {
      title: 'Citizenship',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.citizenship,
      error: '',
      required: true,
    },
    titleVesting: {
      inputType: 'text',
      title: 'Title Vesting',
      required: true,
    },
    email: {
      inputType: 'text',
      type: 'email',
      title: 'Email',
      required: true,
    },
    additionalEmails: {
      inputType: 'EmailList',
      title: 'Additional Emails',
      required: false,
      tooltip: 'Copied on statements and servicing notices. The Email above stays the borrower’s sign-in address.',
      placeholder: 'No additional emails',
    },
    ssn: {
      inputType: 'text',
      type: 'ssn',
      title: 'Social Security Number',
      required: true,
    },
    phone: {
      inputType: 'text',
      type: 'phone',
      title: 'Phone',
      required: true,
    },
    cellPhone: {
      inputType: 'text',
      type: 'phone',
      title: 'Cell Phone',
    },
    homePhone: {
      inputType: 'text',
      type: 'phone',
      title: 'Home Phone',
    },
    workPhone: {
      inputType: 'text',
      type: 'phone',
      title: 'Work Phone',
    },
    dob: {
      inputType: 'text',
      type: 'date',
      title: 'DOB(MM/DD/YYYY)',
      required: true,
    },
    presentAddress: {
      inputType: 'map',
      title: 'Present Address',
      required: true,
      allowForeign: true,
      sitexPro: true,
    },
    mailingAddress: {
      inputType: 'map',
      title: 'Mailing Address',
      required: true,
      allowForeign: true,
    },
    ownership: {
      inputType: 'select',
      title: 'Ownership',
      required: true,
      options: OwnershipTypes,
      hasDefaultOption: true,
    },
    ownedRentedYears: {
      inputType: 'select',
      title: 'Owned/Rented Years',
      required: true,
      options: OwnRentYearOptions,
      hasDefaultOption: true,
    },
    formerAddress: {
      inputType: 'map',
      title: 'Former Address',
      allowForeign: true,
      required: true,
    },
    formerOwnership: {
      inputType: 'select',
      title: 'Former Ownership',
      required: true,
      options: OwnershipTypes,
      hasDefaultOption: true,
    },
    formerOwnedRentedYears: {
      inputType: 'select',
      title: 'Former Address Owned For In Years',
      required: true,
      options: YearOptions,
      hasDefaultOption: true,
    },
    maritalStatus: {
      inputType: 'select',
      title: 'Marital Status',
      required: true,
      options: MaritalStatusTypes,
      hasDefaultOption: true,
    },
    preferredLanguage: {
      inputType: 'select',
      title: 'Preferred Language',
      options: [...LanguageOptions],
      hasDefaultOption: true,
    },
    trustDate: {
      inputType: 'text',
      type: 'date',
      title: 'Date of Trust',
      required: true,
    },
    settlorCount: {
      inputType: 'text',
      type: 'number',
      maxNumberRange: 32,
      title: 'How Many Settlors?',
      required: false,
    },
    hasCoborrower: {
      inputType: 'toggle',
      title: 'Will there be a Co-Borrower or Co-Borrowers?',
    },
    numOfCoBorrowers: {
      inputType: 'text',
      type: 'number',
      title: '# of Other Co-Borrowers',
    },
  }
  for (let i = 0; i < 4; i++) {
    inputs[`settlor${i}`] = {
      inputType: 'text',
      title: 'Settlor Name - #' + (i + 1),
      required: true,
    }
  }
  for (let i = 1; i < 8; i++) {
    inputs[`coborrower${i}Email`] = {
      inputType: 'text',
      type: 'email',
      title: 'Additional Co-Borrower/Guarantor Email Address - #' + i,
    }
  }
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const fieldProps = _fieldProps || loanFieldProperties[FieldOrigin.BorrowerInformation]

  fieldProps &&
    Object.keys(fieldProps).map((key) => {
      if (!isCustomFieldKey(key)) {
        // A built-in field keeps its own definition, but admin owns its option list. Configured
        // options carry a renamed title and a visible flag, so resolve them against the field's
        // own list the way Loan Structure does rather than assigning them straight over.
        const configOptions = fieldProps[key]?.options
        const inputOptions = (inputs[key] as any)?.options
        if (inputOptions && Array.isArray(configOptions) && configOptions.length)
          (inputs[key] as any).options = getNewSelectionOptions(
            inputOptions,
            applyOptionVisibleLogic(configOptions as any) as any,
          )
        return
      }
      inputs[key] = cloneDeep(fieldProps[key] as any)
      if (Array.isArray((inputs[key] as any).options)) {
        ;(inputs[key] as any).options = filterOptionsByVisibleLogic((inputs[key] as any).options)
      }
    })

  return inputs
}

const livingStatusOptions: Record<string, string> = {
  '0': 'Not Assigned',
  '1': 'Own',
  '2': 'Rent',
  '3': 'Living Rent Free',
}

// const yesNoNAOptions: Record<string, string> = {
//   '0': 'Not Assigned',
//   '1': 'Yes',
//   '2': 'No',
// }

export const ResidenceInputs = (): Record<string, InputType> => {
  const inputs: Record<string, InputType> = {
    // residenceID: {
    //   inputType: 'text',
    //   type: 'number',
    //   title: 'Residence ID',
    // },
    // fileDataID: {
    //   inputType: 'text',
    //   type: 'number',
    //   title: 'File Data ID',
    // },
    // borrowerID: {
    //   inputType: 'text',
    //   type: 'number',
    //   title: 'Borrower ID',
    // },
    // displayOrder: {
    //   inputType: 'text',
    //   type: 'number',
    //   title: 'Display Order',
    // },
    // current: {
    //   inputType: 'toggle',
    //   title: 'Current',
    // },
    generalSection: {
      title: 'General',
      inputType: 'section',
      span: 'full',
    },
    livingStatus: {
      title: 'Living Status',
      inputType: 'select',
      hasDefaultOption: true,
      options: livingStatusOptions,
      error: '',
    },
    noYears: {
      inputType: 'text',
      type: 'number',
      title: 'Yrs',
    },
    noMonths: {
      inputType: 'text',
      type: 'number',
      title: 'Mths',
    },
    // street: {
    //   inputType: 'text',
    //   title: 'Street',
    //   maxLength: 50,
    // },
    // city: {
    //   inputType: 'text',
    //   title: 'City',
    //   maxLength: 50,
    // },
    // state: {
    //   title: 'State',
    //   inputType: 'select',
    //   hasDefaultOption: true,
    //   options: [...loanOptionsConstants.states],
    //   error: '',
    // },
    // zip: {
    //   inputType: 'text',
    //   title: 'Zip',
    //   maxLength: 9,
    // },
    // streetContainsUnitNumberOV: {
    //   title: 'Has Unit #',
    //   inputType: 'select',
    //   hasDefaultOption: true,
    //   options: yesNoNAOptions,
    //   error: '',
    // },
    // country: {
    //   inputType: 'text',
    //   title: 'Country',
    //   maxLength: 50,
    // },
    address: {
      inputType: 'map',
      title: 'Address',
      required: true,
      allowForeign: true,
      sitexPro: false,
    },
    monthlyRent: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Monthly Rent',
      tooltip:
        'Enter the share of the rent paid by each applicant.\n\n' +
        `For instance, if Alice and Bruce are a married couple currently renting\n
        an apartment for $1,000 per month, enter $1,000 on Alice’s application and\n
        $0 on Bruce’s application, enter $500 on both applications, or enter some\n
         other split that totals to $1,000. \n
        Do not enter the full $1,000 on both applications because this will cause\n
        the rent to be double-counted in the 'Present PITI' and other calculations.`,
    },

    landlordSection: {
      title: 'Landload Information (Rentals Only)',
      inputType: 'section',
      span: 'full',
    },
    llName: {
      inputType: 'text',
      title: 'Landlord Name',
      maxLength: 50,
    },
    llAttn: {
      inputType: 'text',
      title: 'Attn',
      maxLength: 50,
    },
    llAddress: {
      inputType: 'map',
      title: 'Address',
      required: false,
      allowForeign: false,
      sitexPro: false,
    },
    llPhone: {
      inputType: 'text',
      type: 'phone',
      title: 'Landlord Phone',
      maxLength: 20,
    },
    llFax: {
      inputType: 'text',
      title: 'Fax',
      maxLength: 50,
    },
    notes: {
      inputType: 'textarea',
      title: 'Notes',
      maxLength: 500,
      rows: 5,
      span: 'full',
    },
  }
  return inputs
}
